import {
  Avatar,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { Scrollbar } from './scrollbar';
import { EnhancedTableHead } from './enhanced-table-head';
import EllipsisVerticalIcon from '@heroicons/react/24/outline/EllipsisVerticalIcon';
import { usePopover } from '../hooks/use-popover';
import { useIsMobile } from '../hooks/use-is-mobile';
import { CustomPopOver } from './custom-popover';
import { IOSSwitch } from './IOSSwitch';
import { MobileDataList } from './mobile/mobile-data-list';
import {
  getCellText,
  getPaymentStatusColor,
  getPaymentStatusLabel,
  getRowImage,
  getStatusChipColor,
  getStatusChipLabel,
  isSwitchOn,
} from './table-cell-helpers';

export const CustomTable = (props) => {
  const {
    order,
    orderBy,
    onRequestSort,
    count = 0,
    items = [],
    onPageChange = () => { },
    onRowsPerPageChange,
    onSelectOne,
    onSelect,
    onSelectAll,
    onDeselectOne,
    onDeselectAll,
    page = 0,
    rowsPerPage = 0,
    selected = [],
    headCells,
    popoverItems,
    isLoading,
    switchFunction,
    isSubmitting,
  } = props;
  const popOver = usePopover();
  const isMobile = useIsMobile();
  const selectedSome = (selected.length > 0) && (selected.length < items.length);
  const selectedAll = (items.length > 0) && (selected.length === items.length);

  if (isMobile) {
    return <MobileDataList {...props} />;
  }

  return (
    <>
      {popOver.open &&
        <CustomPopOver
          id={popOver.id}
          anchorEl={popOver.anchorRef}
          open={popOver.open}
          onClose={popOver.handleClose}
          popoverItems={popoverItems}
        />
      }
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
        }}
      >
        <Scrollbar>
          <Box sx={{ minWidth: 800 }}>
            <Table>
              <EnhancedTableHead
                headCells={headCells}
                selectedSome={selectedSome}
                selectedAll={selectedAll}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
                order={order}
                orderBy={orderBy}
                onRequestSort={onRequestSort}
              />
              <TableBody>
                {items.map((row, index) => {
                  const isSelected = selected.includes(row.id);

                  return (
                    <TableRow
                      hover
                      key={index}
                      selected={isSelected}
                      sx={{
                        cursor: 'pointer'
                      }}
                      onClick={(event) => {
                        event.stopPropagation()
                        onSelect(row, true);
                      }}
                    >
                      {onSelectAll &&
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onClick={(event) => {
                              event.stopPropagation()
                            }}
                            onChange={(event) => {
                              if (event.target.checked) {
                                onSelectOne?.(row.id);
                              } else {
                                onDeselectOne?.(row.id);
                              }
                            }}
                          />
                        </TableCell>
                      }
                      {headCells
                        .map((column, index) => {
                          if (column.id === 'name') {
                            return (
                              <TableCell
                                key={index}
                              >
                                <Stack
                                  alignItems="center"
                                  direction="row"
                                  spacing={2}
                                >
                                  <Typography variant="subtitle2">
                                    {getCellText(row, column.id)}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            )
                          } else if (column.id === 'icon' || column.id === 'image_url' || column.id === 'profile_image') {
                            return (
                              <TableCell
                                key={index}
                              >
                                <Avatar
                                  variant='rounded'
                                  alt={column.id === 'profile_image' ? "Profile Picture" : "Preview Picture"}
                                  src={getRowImage(row, column.id)}
                                />
                              </TableCell>
                            )
                          } else if (column.id === 'switch') {
                            return (
                              <TableCell key={index}>
                                {isSubmitting && selected[0]?.id === row.id ?
                                  <CircularProgress
                                    size={26}
                                  /> :
                                  <IOSSwitch
                                    checked={isSwitchOn(row)}
                                    onChange={() => switchFunction(row)}
                                  />
                                }
                              </TableCell>
                            )
                          } else if (column.id === 'order_status' || column.id === 'product_status') {
                            return (
                              <TableCell key={index}>
                                <Chip
                                  label={getStatusChipLabel(row, column.id)}
                                  sx={{
                                    width: 110,
                                  }}
                                  color={getStatusChipColor(row, column.id)}
                                />
                              </TableCell>
                            )
                          } else if (column.id === 'payment_status') {
                            return (
                              <TableCell key={index}>
                                <Chip
                                  label={getPaymentStatusLabel(row)}
                                  sx={{
                                    width: 110,
                                  }}
                                  color={getPaymentStatusColor(row)}
                                />
                              </TableCell>
                            )
                          } else if (column.id === 'location') {
                            return (
                              <TableCell key={index}>
                                {row.region},<br />
                                {row.district},<br />
                                {row.ward &&
                                  <>
                                    {row.ward},<br />
                                  </>
                                }
                                {row.street}.
                              </TableCell>
                            )
                          } else if (column.id === 'actions') {
                            return (
                              <TableCell key={index}>
                                <IconButton
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    popOver.handleOpen(event)
                                    onSelect(row, false)
                                  }}
                                >
                                  <SvgIcon fontSize="small" sx={{ color: "text.primary" }}>
                                    <EllipsisVerticalIcon />
                                  </SvgIcon>
                                </IconButton>
                              </TableCell>
                            )
                          } else {
                            return (
                              <TableCell key={column.id}>
                                {getCellText(row, column.id)}
                              </TableCell>
                            )
                          }
                        })
                      }
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Scrollbar>
        {items.length === 0 && isLoading &&
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
          }}>
            <CircularProgress
              sx={{
                mx: 'auto',
                my: 3,
              }}
            />
          </Box>
        }
        {items.length === 0 && !isLoading &&
          <Typography
            sx={{ my: 3, }}
            align='center'
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            No items
          </Typography>
        }
        <TablePagination
          component="div"
          count={count}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Card>
    </>
  );
};
